// ===========================================
// MESSAGES — the manager side of "Henry + managers"
// ===========================================
// Every employee is a thread. Day-of, time-sensitive comms — running late,
// sick call-outs — the stuff that shouldn't live in a time-off form. Any
// manager can read and reply; messages from staff ping the manager app.

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, MessageSquare, Loader2, Pin } from 'lucide-react';
import clsx from 'clsx';
import { staffMessagesApi } from '../services/api';

const timeOf = (v: string) => {
  const d = new Date(v);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
};

export default function MessagesPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState('');
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: threads = [], isLoading } = useQuery({
    queryKey: ['staff-message-threads'],
    queryFn: async () => (await staffMessagesApi.threads()).data?.data || [],
    refetchInterval: 15_000,
  });
  const { data: thread } = useQuery({
    queryKey: ['staff-message-thread', selected],
    queryFn: async () => (await staffMessagesApi.thread(selected!)).data?.data,
    enabled: !!selected,
    refetchInterval: 8_000,
  });
  const pin = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => staffMessagesApi.pin(id, pinned),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-message-thread', selected] }),
  });
  const send = useMutation({
    mutationFn: () => staffMessagesApi.send(selected!, draft.trim()),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['staff-message-thread', selected] });
      qc.invalidateQueries({ queryKey: ['staff-message-threads'] });
    },
  });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread?.messages?.length]);
  // Opening a thread marks it read server-side — refresh the badge counts.
  useEffect(() => { if (selected) qc.invalidateQueries({ queryKey: ['staff-message-threads'] }); }, [selected, qc]);

  return (
    <div className="p-4 md:p-6 pb-safe h-full">
      <div className="flex items-center gap-2 mb-4">
        <h1 className="text-xl font-bold text-slate-900">Messages</h1>
        <p className="text-sm text-slate-400">one thread per person, shared by all managers</p>
      </div>
      <div className="grid md:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-160px)]">
        {/* thread list */}
        <div className={clsx('card p-0 overflow-y-auto', selected && 'hidden md:block')}>
          {(() => {
            const roles = [...new Set(threads.map((t: any) => t.role).filter(Boolean))].sort() as string[];
            if (roles.length < 2) return null;
            return (
              <div className="px-3 py-2 border-b border-slate-50 sticky top-0 bg-white z-10">
                <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
                  className={clsx('w-full text-xs rounded-lg border px-2 py-1.5 capitalize focus:ring-2 focus:ring-primary-400 focus:border-primary-400',
                    roleFilter ? 'border-primary-300 text-primary-700 font-medium' : 'border-slate-200 text-slate-500')}>
                  <option value="">All positions</option>
                  {roles.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            );
          })()}
          {isLoading && <p className="p-4 text-sm text-slate-400">Loading…</p>}
          {!isLoading && threads.length === 0 && <p className="p-4 text-sm text-slate-400">No active staff yet.</p>}
          {threads.filter((t: any) => !roleFilter || t.role === roleFilter).map((t: any) => (
            <button key={t.employeeId} onClick={() => setSelected(t.employeeId)}
              className={clsx('w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition',
                selected === t.employeeId && 'bg-primary-50/60')}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-slate-900 text-sm truncate">{t.name}</p>
                {t.unread > 0 && <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-primary-600 text-white text-[11px] font-semibold flex items-center justify-center">{t.unread}</span>}
              </div>
              <p className="text-xs text-slate-400 truncate">
                {t.role ? `${t.role} · ` : ''}
                {t.lastMessage ? `${t.lastMessage.sender === 'manager' ? 'You: ' : ''}${t.lastMessage.body}` : 'No messages yet'}
              </p>
            </button>
          ))}
        </div>

        {/* thread */}
        <div className="card p-0 flex flex-col min-h-0">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-300 gap-2">
              <MessageSquare className="w-10 h-10" />
              <p className="text-sm">Pick a person — their thread is shared with every manager.</p>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <button onClick={() => setSelected(null)} className="md:hidden text-sm text-primary-600 mr-1">←</button>
                <p className="font-semibold text-slate-900">{thread?.employee?.name || '…'}</p>
                {thread?.employee?.role && <span className="text-xs text-slate-400">{thread.employee.role}</span>}
              </div>
              {(thread?.messages || []).some((m: any) => m.pinned) && (
                <div className="px-4 py-2 bg-amber-50/70 border-b border-amber-100 space-y-1">
                  {(thread?.messages || []).filter((m: any) => m.pinned).map((m: any) => (
                    <p key={m.id} className="text-xs text-amber-800 flex items-start gap-1.5">
                      <Pin className="w-3 h-3 mt-0.5 shrink-0" /> <span className="min-w-0">{m.body}</span>
                    </p>
                  ))}
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
                {(thread?.messages || []).length === 0 && <p className="text-sm text-slate-400 text-center pt-8">Say hi — they'll see it in their staff app.</p>}
                {(thread?.messages || []).map((m: any) => (
                  <div key={m.id} className={clsx('max-w-[75%] group', m.sender === 'manager' ? 'ml-auto' : '')}>
                    <div className={clsx('rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words relative',
                      m.sender === 'manager' ? 'bg-primary-600 text-white rounded-br-md' : 'bg-slate-100 text-slate-800 rounded-bl-md')}>
                      {m.body}
                      <button onClick={() => pin.mutate({ id: m.id, pinned: !m.pinned })}
                        title={m.pinned ? 'Unpin' : 'Pin — stays visible at the top for both of you'}
                        className={clsx('absolute -top-2 rounded-full p-1 shadow-sm border border-slate-100 bg-white',
                          m.sender === 'manager' ? '-left-2' : '-right-2',
                          m.pinned ? 'text-amber-600' : 'text-slate-300 hidden group-hover:block hover:text-amber-600')}>
                        <Pin className="w-3 h-3" />
                      </button>
                    </div>
                    <p className={clsx('text-[10px] text-slate-400 mt-0.5', m.sender === 'manager' && 'text-right')}>
                      {m.sender === 'manager' && m.senderName ? `${m.senderName} · ` : ''}{timeOf(m.at)}
                    </p>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-slate-100 flex items-end gap-2">
                <textarea rows={1} value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (draft.trim() && !send.isPending) send.mutate(); } }}
                  placeholder="Message…"
                  className="flex-1 resize-none text-sm rounded-xl border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-primary-400 focus:border-primary-400" />
                <button onClick={() => send.mutate()} disabled={!draft.trim() || send.isPending}
                  className="w-9 h-9 rounded-xl bg-primary-600 text-white flex items-center justify-center disabled:opacity-40 hover:bg-primary-700">
                  {send.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
